import React from "react";
import { AppList } from "components/list/AppList";
import { Datagrid, TextField, usePermissions, useTranslate, useNotify, useRefresh, Confirm } from 'react-admin';
import { Button } from "@material-ui/core";

import {stylesForColorInheritInList} from "../../../components/_helpers/useStyles";
import { ScrollingWrapper } from '../../../components/ScrollingWrapper';
import { SelectEnumField } from "components/fields/SelectEnumField";
import { ThirdPartyAppStatus, ThirdPartyAppStatusColors } from "entities/ThirdPartyAppStatus";
import { UserRole } from "entities/UserRole";
import { httpClient } from "api/httpClient";
import { ThirdPartyAppsFilter } from "./ListFilters";


const defaultSort = { field: 'id', order: 'DESC' };

const makeRowStyle = (isPrivileged) => (record) => ({
	height: "48px",
	// Privileged users see the publish-status color; subscribers see a green row when the
	// company is subscribed (reusing the published-green already used in this list).
	backgroundColor: isPrivileged
		? ThirdPartyAppStatusColors[record?.status]
		: record?.is_subscribed
			? ThirdPartyAppStatusColors[ThirdPartyAppStatus.published]
			: undefined,
});

// Company is already subscribed (row is highlighted green): offer only a company-wide
// unsubscribe (revokes the app's access for everyone in the company), behind a confirm.
const UnsubscribeButton = ({ record }) => {
	const translate = useTranslate();
	const notify = useNotify();
	const refresh = useRefresh();
	const [open, setOpen] = React.useState(false);
	const [loading, setLoading] = React.useState(false);

	const handleConfirm = async () => {
		setLoading(true);
		try {
			await httpClient(`/third-party-apps/${record.id}/subscribe`, { method: "DELETE" });
			notify("resources.third-party-apps.subscribe.unsubscribed", "success");
			refresh();
		} catch (err) {
			notify(err.message || "ra.notification.http_error", "error");
		} finally {
			setLoading(false);
			setOpen(false);
		}
	};

	return (
		<>
			<Button color="secondary" onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
				{translate("resources.third-party-apps.subscribe.unsubscribe")}
			</Button>
			<Confirm
				isOpen={open}
				loading={loading}
				title="resources.third-party-apps.subscribe.unsubscribe_title"
				content="resources.third-party-apps.subscribe.unsubscribe_confirm"
				onConfirm={handleConfirm}
				onClose={() => setOpen(false)}
			/>
		</>
	);
};

const SubscribeButton = ({ record }) => {
	const translate = useTranslate();
	const notify = useNotify();
	const [submitting, setSubmitting] = React.useState(false);

	// The app's form_schema fields are filled in on the consent page, so subscribing is a
	// single click: claim the authorize URL and hand the browser straight over.
	const onSubscribe = async (e) => {
		e.stopPropagation();
		setSubmitting(true);
		try {
			const response = await httpClient(`/third-party-apps/${record.id}/subscribe`, { method: "POST" });
			// Full navigation so cookies ride along to the backend-rendered consent page.
			window.location.href = response.json.authorize_url;
		} catch (err) {
			setSubmitting(false);
			notify(err.message || "ra.notification.http_error", "error");
		}
	};

	if (!record) return null;
	// Only authorization_code apps are user-subscribable; client-credentials apps are
	// admin-enabled service integrations, so show no subscribe/unsubscribe/status for them.
	if (!record.grant_types?.includes("authorization_code")) return null;
	// Subscription is company-wide: if anyone in the company already subscribed, every
	// manager sees it as subscribed and can unsubscribe the whole company.
	if (record.is_subscribed) {
		return <UnsubscribeButton record={record} />;
	}
	// Unsubscribing stays available above once the app is unpublished; subscribing does not.
	if (record.status !== ThirdPartyAppStatus.published) return null;
	return (
		<Button color="primary" disabled={submitting} onClick={onSubscribe}>
			{translate("resources.third-party-apps.subscribe.action")}
		</Button>
	);
};


export const ThirdPartyAppsList = props => {
	const classes = stylesForColorInheritInList();
	const { permissions } = usePermissions();

	// Owners/admins manage apps (any status); read-only roles only ever see published apps,
	const isPrivileged = permissions?.role === UserRole.admin || permissions?.role === UserRole.developer;

	return (
		<AppList
			{...props}
			sort={defaultSort}
			exporter={false}
			bulkActionButtons={false}
			classes={classes}
			filters={<ThirdPartyAppsFilter showStatusFilter={isPrivileged} />}
		>
			<ScrollingWrapper>
				<Datagrid rowClick={isPrivileged ? 'show' : false} rowStyle={makeRowStyle(isPrivileged)}>
					<TextField source="id" />
					<TextField source="client_name" />
					{isPrivileged && <SelectEnumField source="status" enumObj={ThirdPartyAppStatus} enumName="third_party_app.status" />}
					{!isPrivileged && <SubscribeButton />}
				</Datagrid>
			</ScrollingWrapper>
		</AppList>
	);
};
